import {
  InputField,
  SelectField,
  ImageUploadField,
  TextareaField,
  PhoneNumberField,
  ColorPickerField,
} from ".././FormFields";
import PageLayout from ".././PageFromLayout";
import { countryCodeOptions } from "../../utils/countryData";
import { FiArrowRight } from "react-icons/fi";
import { useState, useEffect, useRef } from "react";
import Alert from "components/common/Alert";
import { Country, State, City } from "country-state-city";
import { getImageUrl } from "utils/imageHelper";
import { validateForm } from "utils/validation";
import FormSection from ".././FormSection";
import {validateImageFile} from  "utils/common"

export default function MasterCompanyForm({
  initialData,
  onSubmit,
  onBack,
  alert,
  setAlert,
}) {
  // 1. Declare selectedOrganizer FIRST to prevent initialization ReferenceErrors
  const [selectedOrganizer, setSelectedOrganizer] = useState(
    localStorage.getItem("company_id") || ""
  );
  const [submitting, setSubmitting] = useState(false);
  // 2. Initialize state matching field names used in JSX
  const [formData, setFormData] = useState({
    masterCompanyName: "",
    masterCompanyMobileCountryCode: "+91",
    masterCompanyMobile: "",
    masterCompanyEmail: "",
    gstNumber: "",
    masterCompanyLogo: null,

    masterCompanyAddress: "",
    masterCompanyCountry: "",
    masterCompanyState: "",
    masterCompanyDistrict: "",

    adminName: "",
    adminEmail: "",
    adminPassword: "",
    adminMobileCountryCode: "+91",
    adminMobile: "",
    primaryColor: "",
  });

  const isEdit = initialData ? true : false;
  // 1. Define Validation Schema
  const schema = {
    // --- ORGANIZER DETAILS ---
    masterCompanyName: {
      required: true,
      label: "Company Name",
      minLength: 2,
    },
    masterCompanyLogo: {
      required: !isEdit, // Required on create, optional on edit (preserves old logo)
      type: "image",
      label: "Company Logo",
      imageOptions: {
        maxSizeMB: 2,
        allowedTypes: ["image/jpeg", "image/png"],
      },
    },
    masterCompanyMobileCountryCode: {
      required: true,
      label: "Country Code",
    },
    masterCompanyMobile: {
      required: true,
      type: "phone",
      label: "Company Phone Number",
      //   maxLength: 10,
      //   notEqualTo: "adminMobile", // Ensures company mobile !== admin mobile
      //   notEqualMessage: "Must be different from admin mobile",
    },
    masterCompanyEmail: {
      required: true,
      type: "email",
      label: "Company Email",
      //   notEqualTo: "adminEmail", // Ensures company email !== admin email
      //   notEqualMessage: "Must be different from admin email",
    },
    // gstNumber: {
    //   required: false,
    //   label: "GST Number",
    //   minLength: 15,
    //   maxLength: 15,
    // },
    masterCompanyCountry: {
      required: true,
      label: "Country",
    },
    masterCompanyState: {
      required: true,
      label: "State",
    },
    masterCompanyDistrict: {
      required: true,
      label: "District",
    },
    masterCompanyAddress: {
      required: true,
      label: "Company Address",
    },

    // --- ADMIN DETAILS ---
    // adminName: {
    //   required: true,
    //   label: "Admin Name",
    // },
    adminEmail: {
      required: true,
      type: "email",
      label: "Admin Email",
      //   notEqualTo: "masterCompanyEmail",
      //   notEqualMessage: "Must be different from company email",
    },
    // adminMobileCountryCode: {
    //   required: true,
    //   label: "Admin Mobile Country Code",
    // },
    // adminMobile: {
    //   required: true,
    //   type: "phone",
    //   label: "Admin Mobile",
    //   //   maxLength: 10,
    //   //   notEqualTo: "masterCompanyMobile",
    //   //   notEqualMessage: "Must be different from company mobile",
    // },
    adminPassword: {
      required: !isEdit, // Password required only when creating new master company
      type: "password",
      label: "Admin Password",
    },
    primaryColor: {
      required: true,
      label: "Primary Color",
    },
  };

  const [errors, setErrors] = useState({});
  const [existingLogo, setExistingLogo] = useState(null);
  const alertRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((p) => ({
      ...p,
      [name]: "",
    }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    const name = e.target.name;

    if (!file) return;

    const { isValid, error } = validateImageFile(file, 2);

    if (!isValid) {
      setErrors((prev) => ({ ...prev, [name]: error }));
      return;
    }

    setFormData((p) => ({
      ...p,
      [name]: file,
    }));

    setErrors((p) => ({
      ...p,
      [name]: "",
    }));
    setExistingLogo(null);
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    const userId = userData?.userId || "";

    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        masterCompanyLogo: initialData.masterCompanyLogo,
        company_id: selectedOrganizer || initialData.company_id || "",
        created_by: initialData.created_by || userId,
        updated_by: userId,
      }));
      setExistingLogo(
        initialData.masterCompanyLogo
          ? getImageUrl(initialData.masterCompanyLogo)
          : null
      );
    } else {
      setFormData((prev) => ({
        ...prev,
        company_id: selectedOrganizer || "",
        created_by: userId,
        updated_by: userId,
      }));
    }
  }, [initialData, selectedOrganizer]);

  // Country, State, District option calculations
  // 1. Get all countries and format for SelectField (using NAME as value)
  const countryOptions = Country.getAllCountries();
  const countrySelectOptions = countryOptions.map((c) => ({
    value: c.name,
    label: c.name,
  }));

  // 2. Find the ISO code of the selected country name to fetch its states
  const selectedCountryObj = countryOptions.find(
    (c) => c.name === formData.masterCompanyCountry
  );
  const countryIsoCode = selectedCountryObj ? selectedCountryObj.isoCode : "";

  const stateOptions = countryIsoCode
    ? State.getStatesOfCountry(countryIsoCode)
    : [];
  const stateSelectOptions = stateOptions.map((s) => ({
    value: s.name,
    label: s.name,
  }));

  // 3. Find the ISO code of the selected state name to fetch its cities/districts
  const selectedStateObj = stateOptions.find(
    (s) => s.name === formData.masterCompanyState
  );
  const stateIsoCode = selectedStateObj ? selectedStateObj.isoCode : "";

  const districtOptions =
    countryIsoCode && stateIsoCode
      ? City.getCitiesOfState(countryIsoCode, stateIsoCode)
      : [];

  const districtSelectOptions = districtOptions.map((d) => ({
    value: d.name,
    label: d.name,
  }));
  // Handle organizer change event listener
  useEffect(() => {
    const handleOrganizerChange = (e) => {
      setSelectedOrganizer(e.detail);
    };

    window.addEventListener("companyChanged", handleOrganizerChange);
    return () =>
      window.removeEventListener("companyChanged", handleOrganizerChange);
  }, []);

  // Smooth scroll for error alerts
  useEffect(() => {
    if (alert && alertRef.current) {
      alertRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [alert]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    // const schema = getMasterCompanySchema(formData, isEdit);
    const validationErrors = await validateForm(formData, schema);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      // Scroll smoothly to top on validation failure
      if (alertRef.current) {
        alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }
    setSubmitting(true);
    if (onSubmit) {
      onSubmit(formData);
    }
    setSubmitting(false);
  };

  const pageTitle = initialData
    ? "Edit Master Company"
    : "Create Master Company";
  const pageSubTitle = initialData
    ? "Update the master company details below."
    : "Add a new master company to the platform. Fill in the details below.";

  return (
    <PageLayout
      onBack={onBack}
      onSubmit={handleSubmitForm}
      submitText={
        initialData ? "Update Master Company" : "Create  Master Company"
      }
      loading={submitting}
    >
      <form onSubmit={handleSubmitForm} className="flex flex-col gap-6">
        {alert && (
          <div ref={alertRef} className="scroll-mt-[100px] px-6 pt-3">
            <Alert type="error" message={alert} onClose={() => setAlert("")} />
          </div>
        )}
        <FormSection
          title="Company Details"
          // subtitle={`${initialData ? "Update" : "Add"} the main details about the scanner.`}
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <InputField
              label="Company Name"
              name="masterCompanyName"
              value={formData.masterCompanyName || ""}
              onChange={handleChange}
              placeholder="Enter company name"
              error={errors.masterCompanyName}
              required
            />

            <InputField
              label="Email"
              name="masterCompanyEmail"
              value={formData.masterCompanyEmail || ""}
              onChange={handleChange}
              placeholder="Enter email"
              error={errors.masterCompanyEmail}
              required
            />

            <PhoneNumberField
              label="Phone Number"
              name="masterCompanyMobile"
              codeName="phoneCountryCode"
              phoneValue={formData.masterCompanyMobile}
              codeValue={formData.masterCompanyMobileCountryCode}
              onChange={handleChange}
              onCodeChange={(val) => {
                setFormData((prev) => ({
                  ...prev,
                  masterCompanyMobileCountryCode: val,
                }));
                setErrors((p) => ({
                  ...p,
                  masterCompanyMobileCountryCode: "",
                }));
              }}
              options={countryCodeOptions}
              placeholder="712 345 678"
              required
              error={errors.masterCompanyMobile}
            />

            {/* <SelectField
            label="Phone Number Country Code"
            name="masterCompanyMobileCountryCode"
            value={formData.masterCompanyMobileCountryCode || "+91"}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                masterCompanyMobileCountryCode: e.target.value,
              }))
            }
            options={countryCodeOptions}
            placeholder="Search country code..."
            isSearchable={true}
            required
            error={errors.masterCompanyMobileCountryCode}
          />

          <InputField
            label="Phone Number"
            name="masterCompanyMobile"
            type="phone"
            value={formData.masterCompanyMobile}
            onChange={handleChange}
            placeholder="Enter phone number"
            maxLength={10}
            required
            error={errors.masterCompanyMobile}
          /> */}

            <InputField
              label="GST Number"
              name="gstNumber"
              value={formData.gstNumber || ""}
              onChange={handleChange}
              placeholder="Enter GST number"
              error={errors.gstNumber}
            />
            <ColorPickerField
              label="Primary Color"
              name="primaryColor"
              value={formData.primaryColor}
              onChange={handleChange}
              placeholder="#465FFF"
              required
              error={errors.primaryColor}
            />
            <SelectField
              label="Country"
              name="masterCompanyCountry"
              value={formData.masterCompanyCountry || ""}
              onChange={(e) => {
                setFormData((p) => ({
                  ...p,
                  masterCompanyCountry: e.target.value,
                }));
                setErrors((p) => ({
                  ...p,
                  masterCompanyCountry: "",
                }));
              }}
              options={countrySelectOptions}
              placeholder="Search country..."
              isSearchable={true}
              required
              error={errors.masterCompanyCountry}
            />
            <SelectField
              label="State"
              name="masterCompanyState"
              value={formData.masterCompanyState || ""}
              onChange={(e) => {
                setFormData((p) => ({
                  ...p,
                  masterCompanyState: e.target.value,
                }));
                setErrors((p) => ({
                  ...p,
                  masterCompanyState: "",
                }));
              }}
              options={stateSelectOptions}
              placeholder="Search state..."
              isSearchable={true}
              isDisabled={!formData.masterCompanyCountry}
              required
              error={errors.masterCompanyState}
            />
            <SelectField
              label="District"
              name="masterCompanyDistrict"
              value={formData.masterCompanyDistrict || ""}
              onChange={(e) => {
                setFormData((p) => ({
                  ...p,
                  masterCompanyDistrict: e.target.value,
                }));
                setErrors((p) => ({
                  ...p,
                  masterCompanyDistrict: "",
                }));
              }}
              options={districtSelectOptions}
              placeholder="Search district..."
              isSearchable={true}
              isDisabled={!formData.masterCompanyState}
              required
              error={errors.masterCompanyDistrict}
            />
            <TextareaField
              label="Company Address"
              name="masterCompanyAddress"
              value={formData.masterCompanyAddress || ""}
              onChange={handleChange}
              placeholder="Enter address details..."
              rows={3}
              error={errors.masterCompanyAddress}
              required
            />
          </div>
        </FormSection>

        <FormSection title="Company Media">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <ImageUploadField
              label="Company Logo"
              recommended={
                <>
                  Recommended resolution: <b>120 × 40 px</b>. Accepted formats:
                  JPEG, PNG.
                </>
              }
              preview={
                existingLogo
                  ? existingLogo
                  : formData.masterCompanyLogo
                  ? URL.createObjectURL(formData.masterCompanyLogo)
                  : ""
              }
              onUpload={handleFile}
              required={!initialData}
              error={errors.masterCompanyLogo}
              name="masterCompanyLogo"
              accept="image/jpeg, image/png, image/webp, image/heic, image/heif, .heic, .heif"
            />
          </div>
        </FormSection>

        <FormSection title="Admin Details">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <InputField
              label="Admin Name"
              name="adminName"
              value={formData.adminName || ""}
              onChange={handleChange}
              placeholder="Enter admin name"
              error={errors.adminName}
            />
            <InputField
              label="Admin Email"
              name="adminEmail"
              value={formData.adminEmail || ""}
              onChange={handleChange}
              placeholder="Enter admin email"
              error={errors.adminEmail}
              required
            />

            <PhoneNumberField
              label="Admin Mobile"
              name="adminMobile"
              codeName="adminMobileCountryCode"
              phoneValue={formData.adminMobile}
              codeValue={formData.adminMobileCountryCode}
              onChange={handleChange}
              onCodeChange={(val) => {
                setFormData((prev) => ({
                  ...prev,
                  adminMobileCountryCode: val,
                }));
                setErrors((p) => ({
                  ...p,
                  adminMobileCountryCode: "",
                }));
              }}
              options={countryCodeOptions}
              placeholder="712 345 678"
              required
              error={errors.adminMobile}
            />

            {/* <SelectField
            label="Admin Mobile Country Code"
            name="adminMobileCountryCode"
            value={formData.adminMobileCountryCode || "+91"}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                adminMobileCountryCode: e.target.value,
              }))
            }
            options={countryCodeOptions}
            placeholder="Search country code..."
            isSearchable={true}
            error={errors.adminMobileCountryCode}
          />

          <InputField
            label="Admin Mobile"
            name="adminMobile"
            value={formData.adminMobile || ""}
            onChange={handleChange}
            placeholder="Enter admin mobile"
            error={errors.adminMobile}
            type="number"
          /> */}
            <InputField
              label="Password"
              name="adminPassword"
              value={formData.adminPassword || ""}
              onChange={handleChange}
              placeholder="Enter Password"
              error={errors.adminPassword}
              required
              type="password"
            />
          </div>
        </FormSection>

        {/* <div className="button-div flex justify-between">
          <button type="button" onClick={onBack} className="btn-cancel">
            Back
          </button>
          <button type="submit" className="btn-primary">
            <span>
              {initialData ? "Update Master Company" : "Create Master Company"}
            </span>
            <FiArrowRight size={16} />
          </button>
        </div> */}
      </form>
    </PageLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-4 text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">{children}</div>
    </section>
  );
}
